/**
 * SAT MENTOR · Kanonische Salespage · /sat-mentor
 * Verbindlicher Inhalt gemäss FREIGABE · BUILD BLOCK 6 · Abschnitt 3.
 *
 * Hinweis 2026-02-25: Feature-Flag-Gate entfernt — die Salespage ist ein
 * öffentlicher Verkaufsfunnel und muss für potenzielle Kunden immer les-
 * und scrollbar sein. Sichtbarkeit wird nicht mehr per Flag gesteuert.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import {
  S, Section, GoldRule, PrimaryCta, TwoColumn, ChecklistList, Accordion, FooterDisclaimer,
} from "../components/salespage/SalesSections";

export default function SatMentorSalespage() {
  return (
    <div style={{ background: S.midnight, color: S.ivory }} data-testid="page-sat-mentor">
      <Helmet>
        <title>SAT MENTOR · Ausbildung für bewusste Transformationsbegleitung</title>
        <meta name="description" content="Praxisnaher Lehrgang mit 12 Modulen, 67 Video-Slots, 36 Lernbewegungen, HPA, HTC, SAT Deep Dive und einem anspruchsvollen Zertifizierungsweg." />
        <link rel="canonical" href="https://www.sat-akademie.com/sat-mentor" />
        <meta name="robots" content={window.location.hostname.includes("preview") ? "noindex,nofollow" : "index,follow"} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "SAT MENTOR · Ausbildung für bewusste Transformationsbegleitung",
            "description": "12 Module · 67 Video-Slots · 36 Lernbewegungen · 6–12 Monate · Bewerbung und manuelle Zulassung · anspruchsvoller Zertifizierungsweg mit Wissenstest, beobachteter Praxis, Portfolio und Zwei-Personen-Freigabe.",
            "provider": {
              "@type": "Organization",
              "name": "SAT Akademie",
              "url": "https://www.sat-akademie.com"
            },
            "inLanguage": "de",
            "educationalLevel": "Fortgeschritten"
          })}
        </script>
      </Helmet>

      {/* 1. HERO */}
      <Section id="hero" eyebrow="SAT MENTOR">
        <h1
          className="text-4xl md:text-6xl font-serif leading-tight mb-6"
          style={{ color: S.ivory }}
          data-testid="hero-title"
        >
          Ausbildung für bewusste Transformationsbegleitung
        </h1>
        <p
          className="text-lg md:text-2xl leading-relaxed max-w-3xl mb-6"
          style={{ color: S.goldSoft }}
          data-testid="hero-tagline"
        >
          Aus eigener Reife wird verantwortliche Wirkung.
        </p>
        <p
          className="text-base md:text-lg leading-relaxed max-w-3xl mb-10"
          style={{ color: S.ivoryMuted }}
          data-testid="hero-sub"
        >
          Ein praxisnaher Lehrgang für Menschen, die andere würdevoll, klar und sicher
          begleiten wollen — mit Haltung, HPA, HTC, SAT Deep Dive, Fallführung,
          Supervision und einem anspruchsvollen Zertifizierungsweg.
        </p>
        <PrimaryCta
          href="/sat-mentor/bewerbung"
          label="Für SAT MENTOR bewerben"
          testId="hero-cta"
        />
        <p className="mt-6 text-xs" style={{ color: "rgba(246,241,231,0.6)" }}>
          12 Module · 67 Video-Slots · 36 Lernbewegungen · 6–12 Monate (Premium-Empfehlung 9 Monate).
        </p>
      </Section>

      {/* 1b. HAMSTERRAD → BERUFUNG (neu) */}
      <Section id="hamsterrad" eyebrow="Wenn das Alte nicht mehr trägt">
        <h2
          className="text-3xl md:text-5xl font-serif leading-tight mb-8 max-w-3xl"
          style={{ color: S.ivory }}
          data-testid="hamsterrad-title"
        >
          Hamsterrad satt? Bereit für ein <span style={{ color: S.gold }}>sinnvolles Handwerk</span>?
        </h2>
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
          <div className="space-y-5 text-base md:text-lg leading-relaxed" style={{ color: S.ivoryMuted }}>
            <p>
              Du funktionierst. Du lieferst. Du bist erfolgreich <em>auf Papier</em>.
              Und trotzdem meldet sich eine Frage, die du kaum noch stumm schalten kannst:
            </p>
            <p className="serif italic text-xl md:text-2xl" style={{ color: S.goldSoft }}>
              &bdquo;Ist das alles?&ldquo;
            </p>
            <p>
              Du hast dich verändert. Menschen kommen zu dir, weil sie deine Klarheit,
              deine Tiefe, deine Wahrhaftigkeit spüren. Freunde sagen: <em>&bdquo;Du hast so
              etwas an dir — bei dir kann man Dinge sagen, die man sonst nirgends sagt.&ldquo;</em>
            </p>
            <p>
              Nur — <span className="text-white">du kannst nicht mehr weiter so tun,
              als hätte das nichts mit deinem Weg zu tun.</span>
            </p>
          </div>
          <div className="space-y-4">
            <div className="p-6 rounded-xl border" style={{ borderColor: S.line, background: S.midnightSoft }}>
              <div className="text-xs tracking-[0.3em] uppercase mb-2" style={{ color: S.gold }}>Was du dir wünschst</div>
              <ul className="space-y-2 text-sm md:text-base" style={{ color: S.ivory }}>
                <li>→ Eine Arbeit, die Sinn hat und Menschen wirklich hilft.</li>
                <li>→ Ein Handwerk, das trägt — auch wenn Märkte kippen.</li>
                <li>→ Eine Rolle, in der deine Sensibilität nicht Schwäche, sondern <span style={{ color: S.gold }}>dein wichtigstes Werkzeug</span> ist.</li>
                <li>→ Ein Weg mit Rahmen, Ethik und Verantwortung — nicht mit Instagram-Coaching-Optik.</li>
              </ul>
            </div>
            <div className="p-6 rounded-xl border" style={{ borderColor: S.line, background: S.midnightSoft }}>
              <div className="text-xs tracking-[0.3em] uppercase mb-2" style={{ color: S.goldSoft }}>Was SAT MENTOR gibt</div>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: S.ivoryMuted }}>
                Nicht noch eine Weiterbildung. Sondern die <span className="text-white">Übersetzung dessen,
                was in dir längst gereift ist, in eine Form, die andere sicher hält.</span>
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 1c. HOCHSENSIBILITÄT + KI-INVASION · USP */}
      <Section id="ki-invasion" eyebrow="Der Zeitpunkt" title="Im Zeitalter der KI wird das Menschliche zum wertvollsten Handwerk">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
          <div className="space-y-5 text-base md:text-lg leading-relaxed" style={{ color: S.ivoryMuted }}>
            <p>
              Berufe verschwinden schneller, als sich Menschen darauf einstellen können.
              Analysen, Texte, Konzepte, Diagnosen — die KI übernimmt.
            </p>
            <p>
              <span className="text-white">Was sie nicht übernehmen kann:</span> präsent
              neben einem Menschen sitzen, dessen Nervensystem zittert. Etwas spüren,
              was nicht gesagt wird. Einen Raum halten, in dem jemand endlich weinen darf.
            </p>
            <p>
              Genau das ist die Zukunft <em>echter</em> Begleitung.
              Und genau dafür brauchst du keine Ausbildung, die noch mehr Techniken oben drauf schichtet —
              sondern <span style={{ color: S.gold }}>eine, die deine Feinfühligkeit in professionelle Sicherheit verwandelt.</span>
            </p>
          </div>
          <div className="p-6 md:p-8 rounded-2xl border-2" style={{ borderColor: S.gold, background: `${S.gold}0d` }}>
            <div className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: S.gold }}>Hochsensibilität als Stärke</div>
            <p className="serif text-xl md:text-2xl leading-snug mb-4" style={{ color: S.ivory }}>
              &bdquo;Wer viel spürt, kann viel halten — wenn er gelernt hat, wo Halten aufhört und Grenze beginnt.&ldquo;
            </p>
            <p className="text-sm leading-relaxed" style={{ color: S.ivoryMuted }}>
              SAT MENTOR macht Sensitivität zum Werkzeug. Kein Reframing. Kein Verstecken.
              Sondern <span className="text-white">Ethik, Rahmen und Werkzeug</span>, damit deine
              Wahrnehmung anderen dient — ohne dich zu leeren.
            </p>
          </div>
        </div>
      </Section>

      {/* 2. AUSGANGSLAGE */}
      <Section id="context" eyebrow="Ausgangslage" title="Wenn Reife zur Verantwortung wird">
        <p className="text-base md:text-lg leading-relaxed" style={{ color: S.ivoryMuted }}>
          Du hast bereits deinen eigenen Transformationsweg gegangen. Menschen kommen zu dir,
          weil sie deine Klarheit spüren. Jetzt willst du das, was in dir gereift ist, in eine
          Form bringen, die andere sicher hält — mit Haltung, methodischem Werkzeug und
          professioneller Verantwortung.
        </p>
      </Section>

      {/* 3. SAT-WENDEPUNKT */}
      <Section id="turningpoint" eyebrow="SAT-Wendepunkt" title="Wirkung braucht Struktur">
        <TwoColumn
          left={
            <p className="text-base leading-relaxed" style={{ color: S.ivoryMuted }}>
              SAT MENTOR verbindet innere Haltung mit klaren Werkzeugen und einem
              belastbaren Sicherheitsrahmen. Fallverläufe, Supervision und beobachtete
              Praxis machen sichtbar, was du wirklich kannst — und wo dein Wachstumsfeld liegt.
            </p>
          }
          right={
            <ChecklistList
              items={[
                "Haltung vor Technik",
                "HPA und HTC als methodischer Kompass",
                "SAT Deep Dive für dichte, ehrliche Begleitung",
                "Fallverläufe, Peer-Praxis, Supervision",
              ]}
              testIdPrefix="turning"
            />
          }
        />
      </Section>

      {/* 4. FÜR WEN */}
      <Section id="fitness" eyebrow="Passung" title="Für wen SAT MENTOR geeignet ist – und für wen nicht">
        <TwoColumn
          left={
            <>
              <h3 className="text-lg mb-4" style={{ color: S.gold }}>Geeignet</h3>
              <ChecklistList
                items={[
                  "Menschen mit eigener Transformationserfahrung (SAT MASTER oder anerkannte Grundlage)",
                  "Bereitschaft zur Bewerbung und manuellen Zulassung",
                  "Bereitschaft zu Fallverläufen, Peer-Praxis und Supervision",
                  "Verpflichtung zu Ethik und Sicherheitsstandard",
                ]}
                testIdPrefix="fit-yes"
              />
            </>
          }
          right={
            <>
              <h3 className="text-lg mb-4" style={{ color: S.goldSoft }}>Nicht geeignet</h3>
              <ChecklistList
                items={[
                  "Wer eine schnelle Zertifizierung sucht — Zertifikat niemals automatisch",
                  "Wer keine Bereitschaft für 6–12 Monate ernsthafte Arbeit hat",
                  "Wer eine staatliche Fachausbildung erwartet — SAT MENTOR ist keine medizinische, psychologische, psychiatrische oder psychotherapeutische Ausbildung",
                  "Wer keine Supervision zulassen möchte",
                ]}
                testIdPrefix="fit-no"
              />
            </>
          }
        />
      </Section>

      {/* 5. CURRICULUM */}
      <Section id="curriculum" eyebrow="Aufbau" title="12 Module · 67 Video-Slots · 36 Lernbewegungen">
        <Accordion
          testIdPrefix="mentor-curriculum"
          items={[
            { title: "Modul 1 · Identität und Haltung", body: "Wer du bist als Begleiter — vor jeder Methode." },
            { title: "Modul 2 · Ethik, Sicherheit und Grenzen", body: "Der Ethikkodex, Sicherheitscheck und Grenzen deiner Verantwortung." },
            { title: "Modul 3 · Psychologie und Nervensystem", body: "Was im Menschen wirkt — ohne Diagnose, mit klarem Verständnis." },
            { title: "Modul 4 · Gesprächsführung und Fokuskompass", body: "Klar, präsent, würdevoll." },
            { title: "Modul 5 · SAT-HPA · Human Design als Kompass", body: "Potenzial-Erkennung ohne Deutungshoheit." },
            { title: "Modul 6 · SAT-HTC · Human Transformation Coaching", body: "Methodisches Werkzeug für dichte Prozesse." },
            { title: "Modul 7 · SAT Deep Dive I · Sicherheitsgate", body: "Öffnet erst nach Modulen 1–4, anerkanntem Ethikkodex, bestandenem Sicherheitscheck und manueller Ausbildungsfreigabe." },
            { title: "Modul 8 · SAT Deep Dive II", body: "Vertiefung mit erhöhter methodischer Anforderung." },
            { title: "Modul 9 · SAT Deep Dive III", body: "Meisterlicher Prozess mit voller Verantwortung." },
            { title: "Modul 10 · Beziehung und systemische Felder", body: "Die Person im Kontext — Familie, Beziehung, System." },
            { title: "Modul 11 · Fallführung und Praxis", body: "Fallverläufe, Peer-Praxis, Supervision." },
            { title: "Modul 12 · Mentor-Reife und Zertifizierung", body: "Portfolio, Wissenstest, beobachtete Praxis, Mentor-Manifest, Zwei-Personen-Freigabe." },
          ]}
        />
      </Section>

      {/* 6. LEISTUNGEN */}
      <Section id="deliverables" eyebrow="Enthaltene Leistungen" title="Was du bekommst">
        <ChecklistList
          testIdPrefix="deliverables"
          items={[
            "67 Video-Slots über 12 Module verteilt",
            "36 Lernbewegungen (drei pro Modul)",
            "Live-Lehrimpulse, Demonstrationen, Peer-Praxis",
            "Regelmässige Supervision und Intervision",
            "Portfolio-Führung, Wissenstest und beobachtete Praxis",
            "Modul-7-Sicherheitsgate mit expliziter Ausbildungsfreigabe",
            "Rezertifizierungsrahmen nach zwei Jahren",
          ]}
        />
      </Section>

      {/* 7. ABLAUF */}
      <Section id="flow" eyebrow="Ablauf" title="Vom Erstkontakt bis zum Zertifikat">
        <ol className="space-y-4" data-testid="flow-list">
          {["Bewerbung mit motivations- und praxisorientierten Fragen.",
            "Persönliche Sichtung durch die Ausbildungsleitung.",
            "Zulassungsgespräch — beide Seiten prüfen die Passung.",
            "Nach APPROVED und bestätigter Zahlung: Start in der Kohorte.",
            "6–12 Monate Lehrgang mit Peer-Praxis und Supervision.",
            "Portfolio, Wissenstest, beobachtete Praxis — jedes Kriterium einzeln geprüft.",
            "Manuelle Zwei-Personen-Freigabe. Zertifikat niemals automatisch.",
          ].map((step, i) => (
            <li
              key={step}
              className="flex gap-4 items-start"
              style={{ color: S.ivoryMuted }}
            >
              <span
                className="font-serif text-2xl"
                style={{ color: S.gold, flexShrink: 0, width: "2rem" }}
                aria-hidden
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-base leading-relaxed pt-1">{step}</span>
            </li>
          ))}
        </ol>
      </Section>

      {/* 8. ÜBER NENAD */}
      <Section id="nenad" eyebrow="Über" title="Nenad Vladovic · Ausbildungsleitung">
        <p className="text-base leading-relaxed max-w-3xl" style={{ color: S.ivoryMuted }}>
          Nenad Vladovic begleitet seit über zwei Jahrzehnten Menschen in ihrem
          Transformationsprozess. SAT MENTOR ist die Weitergabe dieser Arbeit in
          strukturierter Form — mit einem Sicherheitsrahmen, der auch das schützt,
          was zwischen den Zeilen geschieht.
        </p>
      </Section>

      {/* 9. INVESTITION */}
      <Section id="investment" eyebrow="Investition" title="Bewerbung statt Direktkauf">
        <p className="text-base leading-relaxed" style={{ color: S.ivoryMuted }}>
          SAT MENTOR wird ausschliesslich über eine Bewerbung und ein Zulassungsgespräch
          vergeben. Die Investition, Ratenrahmen und Konditionen werden im Gespräch
          — nach bestätigter Passung — besprochen.
        </p>
        <div className="mt-8">
          <PrimaryCta
            href="/sat-mentor/bewerbung"
            label="Für SAT MENTOR bewerben"
            testId="investment-cta"
          />
        </div>
      </Section>

      {/* 11. FAQ */}
      <Section id="faq" eyebrow="FAQ" title="Häufige Fragen">
        <Accordion
          testIdPrefix="mentor-faq"
          items={[
            { title: "Wie viel Zeit brauche ich?", body: "Rechne mit 8–12 Stunden pro Woche für Lehrimpulse, Peer-Praxis, Supervision und Portfolio-Arbeit." },
            { title: "Ist Teilnahme an Live-Elementen freiwillig?", body: "Selbst-Offenlegung ist freiwillig. Live-Teilnahme selbst ist Teil des Lehrgangs. Beteiligung an Übungen bleibt jederzeit freiwillig." },
            { title: "Kann ich eine Übung stoppen?", body: "Ja. Du bestimmst dein Tempo. Es gibt kein »durchhalten müssen«." },
            { title: "Ist SAT MENTOR eine Therapieausbildung?", body: "Nein. SAT MENTOR ist keine medizinische, psychologische, psychiatrische oder psychotherapeutische Fachausbildung und ersetzt keine staatliche Zulassung." },
            { title: "Werden Laborwerte oder Diagnosen übermittelt?", body: "Nein. Weder von dir noch von deinen zukünftigen Klienten. SAT arbeitet ohne medizinische Diagnostik." },
            { title: "Wann bekomme ich Zugang zu den Inhalten?", body: "Erst nach APPROVED-Bewerbung und bestätigter Zahlung. Eine Bewerbung oder ein Konto allein erzeugt keinen Zugang und kein Entitlement." },
            { title: "Gibt es Raten?", body: "Ratenmodelle werden nur angeboten, wenn technisch und vertraglich sauber konfiguriert. Andernfalls: Vollzahlung oder individuelle Ratenvereinbarung." },
            { title: "Wann bekomme ich das Mentor-Zertifikat?", body: "Erst nach vollständiger manueller Prüfung aller 15 Kriterien inkl. Wissenstest ≥ 80 %, beobachteter Praxis ≥ 80 %, allen sicherheitskritischen Kriterien und einer manuellen Zwei-Personen-Freigabe. Ein Kursabschluss allein ist kein Zertifikat." },
            { title: "Wie lange gilt das Zertifikat?", body: "Rezertifizierung nach zwei Jahren." },
            { title: "Wie werden Videos ausgeliefert?", body: "Ausschliesslich als geschützter Stream. PDFs stehen nur lektionsweise zur Verfügung." },
            { title: "Was passiert bei Zahlungsausfall?", body: "Wir suchen zuerst das Gespräch. Bei anhaltendem Ausfall wird der Zugang gemäss vertraglicher Regelung eingeschränkt." },
          ]}
        />
      </Section>

      <GoldRule />

      <Section id="final-cta">
        <div className="text-center">
          <h2
            className="text-3xl md:text-5xl font-serif mb-8"
            style={{ color: S.ivory }}
            data-testid="final-cta-title"
          >
            Bereit für den Ausbildungsweg?
          </h2>
          <PrimaryCta
            href="/sat-mentor/bewerbung"
            label="Für SAT MENTOR bewerben"
            testId="final-cta-button"
          />
        </div>
      </Section>

      <FooterDisclaimer />
    </div>
  );
}
